/**
 * @file React hook — useExpertSubmissions.ts: form/state logic for contributor dashboard.
 */

import { useEffect, useMemo, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";
import { API_URLS } from "../../../../../constants/apiUrls";
import {
  enrichExpertDatasets,
  getExpertDatasetsByMaterial,
  getExpertSubmissionsByAssignedMaterial,
  mapExpertDatasetToTableRow,
  type ExpertDataset,
  type ExpertDatasetTableRow,
} from "../../../../../services/expertDatasetService";

/** Type definition — Submission Row. */
export type SubmissionRow = ExpertDatasetTableRow;

interface ExpertDatasetsResponse {
  success: boolean;
  message?: string;
  count?: number;
  data?: ExpertDataset[];
  assigned?: {
    count?: number;
    data?: ExpertDataset[];
  };
  byMaterial?: {
    count?: number;
    data?: ExpertDataset[];
  };
}

/** Prefer GET /expert/submissions; fall back if route missing (older backends). */
async function fetchExpertQueueDatasets(): Promise<ExpertDataset[]> {
  const fromLegacy = async () => {
    const { data } = await getExpertSubmissionsByAssignedMaterial({
      page: 1,
      pageSize: 1000,
    });
    return data;
  };

  try {
    const res = await axiosClient.get<ExpertDatasetsResponse>(API_URLS.EXPERT.SUBMISSIONS);
    const body = res.data;
    if (body?.success) {
      const payload =
        body.data ??
        body.assigned?.data ??
        body.byMaterial?.data ??
        [];
      return Array.isArray(payload) ? payload : [];
    }
    const msg = String(body?.message ?? "").toLowerCase();
    if (msg.includes("route not found") || msg.includes("not found")) {
      return fromLegacy();
    }
    throw new Error(body?.message || "Failed to load submissions");
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number; data?: { message?: string } } };
    if (ax?.response?.status === 404) {
      return fromLegacy();
    }
    const m = String(ax?.response?.data?.message ?? "").toLowerCase();
    if (m.includes("route not found") || m.includes("not found")) {
      return fromLegacy();
    }
    throw err;
  }
}

/** Hook: use Expert Submissions. */
export const useExpertSubmissions = (materialId: number | null | undefined) => {
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  /** All datasets for the material (same source as All Dataset graph). */
  const [chartDatasets, setChartDatasets] = useState<ExpertDataset[]>([]);
  /** Submission IDs in the review queue (black overlay on the chart). */
  const [reviewHighlightIds, setReviewHighlightIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const materialName = useMemo(() => {
    const match = (chartDatasets || []).find((d) => d.background?.material?.name);
    return match?.background?.material?.name || "";
  }, [chartDatasets]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [queuePayload, allByMaterialRaw] = await Promise.all([
          fetchExpertQueueDatasets(),
          getExpertDatasetsByMaterial(),
        ]);
        if (cancelled) return;

        const hasMaterialFilter = materialId !== null && materialId !== undefined;
        const filterByMaterial = (d: ExpertDataset) => {
          if (!hasMaterialFilter) return true;
          return d.background?.materialId === materialId;
        };

        const queueFiltered = (queuePayload || []).filter(filterByMaterial);
        const allFiltered = (allByMaterialRaw || []).filter(filterByMaterial);

        const [enrichedQueue, enrichedAll] = await Promise.all([
          enrichExpertDatasets(queueFiltered),
          enrichExpertDatasets(allFiltered),
        ]);

        if (cancelled) return;

        setChartDatasets(enrichedAll);

        const mapped = enrichedQueue.map((d) => mapExpertDatasetToTableRow(d));

        if (cancelled) return;
        setRows(mapped);
        setReviewHighlightIds(mapped.map((r) => r.submissionId));
      } catch (err: unknown) {
        console.error("Error fetching submissions:", err);
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        if (!cancelled) {
          setRows([]);
          setChartDatasets([]);
          setReviewHighlightIds([]);
          setError(
            ax?.response?.data?.message ||
              ax?.message ||
              "Failed to load submissions"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [materialId]);

  return { rows, chartDatasets, reviewHighlightIds, materialName, loading, error };
};
